import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { config } from "@/shared/lib/config";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  Field,
  FormCheckbox,
  FormInput,
  FormRoot,
  FormTextarea,
  Modal,
  RHF,
  confirm,
  zodResolver,
} from "@devhop/ui";
import { PlusIcon, Settings, SparklesIcon } from "lucide-react";
import * as React from "react";
import { z } from "zod";
import {
  CreateQCStandardFormSchema,
  QCStandardItemFormSchema,
} from "../../domain/contracts";
import type { QCWorkTypeDTO } from "../../domain/contracts/work-type";
import {
  useCreateQCWorkType,
  useDeleteQCWorkType,
  useQCWorkTypes,
  useUpdateQCWorkType,
} from "../api/queries";
import { QCInspectorUserIdsField } from "./QCInspectorUserIdsField";
import { QCItemsField } from "./QCItemsField";
import { QCWorkTypeForm } from "./QCWorkTypeForm";
import { qcFormSamples } from "./qcFormSamples";

const QCFormSchema = CreateQCStandardFormSchema.extend({
  items: z.array(QCStandardItemFormSchema).optional().default([]),
});

export type QCFormValues = z.infer<typeof QCFormSchema>;

type DepartmentItem = { id: string; name: string };
type WorkTypeItem = LookupItem;
type PositionItem = LookupItem;

const statusOptions = [
  { value: "active", label: "ໃຊ້ງານ" },
  { value: "draft", label: "ປັບປຸງ" },
  { value: "inactive", label: "ເລີກໃຊ້" },
];

export function QCForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<QCFormValues>;
  onSubmit: (values: QCFormValues) => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<QCFormValues>({
    resolver: zodResolver(QCFormSchema) as any,
    defaultValues: {
      name: "",
      description: null,
      workTypeId: "",
      workStage: null,
      departmentId: null,
      inspectorPositionId: null,
      status: "draft",
      requirePhoto: false,
      requireNotesOnFail: true,
      passCriteria: null,
      failCriteria: null,
      actionOnFail: null,
      notifyPositionId: null,
      checklistId: null,
      kpiId: null,
      inspectorUserIds: [],
      items: [],
      ...initialValues,
    },
  });

  const [sampleIndex, setSampleIndex] = React.useState(0);
  const isDev =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  // Get first work type for workTypeId
  React.useEffect(() => {
    const fetchWorkType = async () => {
      try {
        const result = await fetch(
          `${config.apiUrl}/qc/work-types/lookup?limit=1&skip=0`,
          {
            credentials: "include",
          },
        );
        if (result.ok) {
          const data = await result.json();
          const firstWorkType = data.items?.[0];
          if (firstWorkType?.id && !methods.getValues().workTypeId) {
            methods.setValue("workTypeId", firstWorkType.id);
          }
        }
      } catch {
        // If fetching fails, leave workTypeId empty (user can select manually)
      }
    };
    if (isDev && !initialValues?.workTypeId) {
      fetchWorkType();
    }
  }, [isDev, initialValues?.workTypeId, methods]);

  const departmentId = RHF.useWatch({
    control: methods.control,
    name: "departmentId",
  });
  const inspectorPositionId = RHF.useWatch({
    control: methods.control,
    name: "inspectorPositionId",
  });

  const prevDepartmentIdRef = React.useRef<typeof departmentId>(undefined);
  React.useEffect(() => {
    if (
      prevDepartmentIdRef.current !== undefined &&
      prevDepartmentIdRef.current !== departmentId
    ) {
      methods.setValue("inspectorPositionId", null);
      methods.setValue("inspectorUserIds", []);
    }
    prevDepartmentIdRef.current = departmentId;
  }, [departmentId, methods]);

  const prevInspectorPositionIdRef = React.useRef<typeof inspectorPositionId>(
    undefined,
  );
  React.useEffect(() => {
    if (
      prevInspectorPositionIdRef.current !== undefined &&
      prevInspectorPositionIdRef.current !== inspectorPositionId
    ) {
      methods.setValue("inspectorUserIds", []);
    }
    prevInspectorPositionIdRef.current = inspectorPositionId;
  }, [inspectorPositionId, methods]);

  const handleAutoFill = () => {
    const sample = qcFormSamples[sampleIndex];
    if (sample) {
      Object.entries(sample).forEach(([key, value]) => {
        methods.setValue(key as keyof QCFormValues, value as never);
      });
      setSampleIndex((prev) => (prev + 1) % qcFormSamples.length);
    }
  };

  const handleSubmit = (status: "draft" | "active") => {
    methods.handleSubmit((values) => {
      onSubmit({ ...values, status });
    })();
  };

  // Work Type Management Component
  function WorkTypeManagementButton() {
    const canManage = useActionPermission([
      "qc:create",
      "qc:update",
      "qc:delete",
    ]);
    const listModal = useDisclosure();
    const createModal = useDisclosure();
    const editModal = useDisclosure<QCWorkTypeDTO>();
    const listQuery = useQCWorkTypes({ limit: 100, offset: 0 });
    const createWorkType = useCreateQCWorkType();
    const updateWorkType = useUpdateQCWorkType();
    const deleteWorkType = useDeleteQCWorkType();

    if (!canManage) return null;

    return (
      <>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => listModal.open()}
          title="ຈັດການປະເພດງານ"
          className="mt-6"
        >
          <Settings className="h-4 w-4" />
        </Button>

        {/* Work Types List Modal */}
        <Modal
          open={!!listModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              listModal.close();
              createModal.close();
              editModal.close();
            }
          }}
          title="ຈັດການປະເພດງານ"
          description="ສ້າງ, ແກ້ໄຂ, ຫຼື ລຶບປະເພດງານ QC"
          size="md"
        >
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  listModal.close();
                  createModal.open();
                }}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                ສ້າງປະເພດງານໃໝ່
              </Button>
            </div>

            {listQuery.isLoading ? (
              <div className="py-8 text-center text-muted-foreground">
                ກຳລັງໂຫຼດ...
              </div>
            ) : listQuery.data?.data.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                ຍັງບໍ່ມີປະເພດງານ
              </div>
            ) : (
              <div className="space-y-2">
                {listQuery.data?.data.map((workType) => (
                  <div
                    key={workType.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{workType.name}</span>
                      {workType.description && (
                        <span className="text-muted-foreground text-xs">
                          {workType.description}
                        </span>
                      )}
                      <span className="text-muted-foreground text-xs">
                        ລໍາດັບ: {workType.orderIndex}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          listModal.close();
                          editModal.openWith(workType);
                        }}
                      >
                        ແກ້ໄຂ
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບປະເພດງານ "${workType.name}"?`,
                              description: "ປະເພດງານນີ້ຈະລຶບອອກຈາກລາຍການທັງຫມົດ",
                              actionText: "ລຶບ",
                              ActionProps: {
                                variant: "destructive",
                              },
                            })
                          ) {
                            await deleteWorkType.mutateAsync(workType.id);
                          }
                        }}
                      >
                        ລຶບ
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>

        {/* Create Work Type Modal */}
        <Modal
          open={!!createModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              createModal.close();
            } else {
              listModal.close();
            }
          }}
          title="ສ້າງປະເພດງານ"
          description="ກໍານົດຊື່, ຄໍາອະທິບາຍ, ແລະລໍາດັບຂອງປະເພດງານ."
          size="sm"
        >
          <QCWorkTypeForm
            onSubmit={async (vals) => {
              await createWorkType.mutateAsync({
                name: vals.name || "",
                description: vals.description ?? null,
                orderIndex: vals.orderIndex ?? 0,
              });
              createModal.close();
              listModal.open();
            }}
            submitting={createWorkType.isPending}
          />
        </Modal>

        {/* Edit Work Type Modal */}
        <Modal
          open={!!editModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              editModal.close();
            } else {
              listModal.close();
            }
          }}
          title="ແກ້ໄຂປະເພດງານ"
          description="ປັບປຸງລາຍລະອຽດຂອງປະເພດງານ."
          size="sm"
        >
          <QCWorkTypeForm
            initialValues={
              editModal.data
                ? {
                  name: editModal.data.name,
                  description: editModal.data.description,
                  orderIndex: editModal.data.orderIndex,
                }
                : undefined
            }
            onSubmit={async (vals) => {
              if (!editModal.data) return;
              await updateWorkType.mutateAsync({
                id: editModal.data.id,
                input: {
                  name: vals.name || "",
                  description: vals.description ?? null,
                  orderIndex: vals.orderIndex ?? 0,
                },
              });
              editModal.close();
              listModal.open();
            }}
            submitting={updateWorkType.isPending}
          />
        </Modal>
      </>
    );
  }

  return (
    <FormRoot<QCFormValues>
      methods={methods}
      onSubmit={(values) => onSubmit(values)}
      className="space-y-6"
    >
      {/* Section 1: ข้อมูลมาตรฐาน */}
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <h2 className="font-semibold text-lg">1. ຂໍ້ມູນມາດຕະຖານ</h2>
        <div data-tourid="form-name">
          <FormInput
            name="name"
            label="ຊື່ມາດຕະຖານ QC"
            requiredMark
            placeholder="ຕົວຢ່າງ: ມາດຕະຖານ QC ການຜະລິດ"
          />
        </div>
        <div data-tourid="form-description">
          <FormTextarea
            name="description"
            label="ຄໍາອະທິບາຍ"
            placeholder="ອະທິບາຍເພີ່ມເຕີມ (ບໍ່ບັງຄັບ)"
            rows={3}
          />
        </div>
        <div className="flex items-center gap-2" data-tourid="form-work-type">
          <FormInfiniteCombobox<WorkTypeItem>
            queryKey={["qc-work-types", "form"]}
            name="workTypeId"
            label="ເລືອກງານ / ຂັ້ນຕອນ"
            requiredMark
            queryFn={(args) =>
              fetchLookupForInfinite(`${config.apiUrl}/qc/work-types/lookup`, {
                search: args.search,
                pageParam: args.pageParam,
              })
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/qc/work-types/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກປະເພດງານ"
            className="flex-1"
          />
          <WorkTypeManagementButton />
        </div>
        <div data-tourid="form-work-stage">
          <FormInput
            name="workStage"
            label="ຂັ້ນຕອນການດໍາເນີນງານ"
            placeholder="ຕົວຢ່າງ: ກ່ອນແພັກ, ຫຼັງຜະລິດ (ບໍ່ບັງຄັບ)"
          />
        </div>
        <div data-tourid="form-department">
          <FormInfiniteCombobox<DepartmentItem>
            name="departmentId"
            label="ເລືອກພະແນກ"
            queryKey={["qc-departments"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/qc/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/qc/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກພະແນກ (ບໍ່ບັງຄັບ)"
            clearable
          />
        </div>
        <div data-tourid="form-inspector-position">
          <FormInfiniteCombobox<PositionItem>
            name="inspectorPositionId"
            label="ຕໍາແໜ່ງຜູ້ກວດສອບ (ຕົວເລືອກ)"
            queryKey={["qc-positions-inspector", departmentId ?? ""]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/qc/positions/lookup`,
                args,
                departmentId ? { departmentId } : undefined,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/qc/positions/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກຕໍາແໜ່ງ (ບໍ່ບັງຄັບ)"
            clearable
          />
        </div>
        <div data-tourid="form-inspector-users">
          <QCInspectorUserIdsField
            departmentId={departmentId}
            inspectorPositionId={inspectorPositionId}
          />
        </div>
      </div>

      {/* Section 2: รายการตรวจ */}
      <div
        className="space-y-4 rounded-lg border bg-card p-4"
        data-tourid="form-items"
      >
        <h2 className="font-semibold text-lg">2. ລາຍການກວດສອບ</h2>
        <QCItemsField
          control={methods.control}
          watch={methods.watch}
          setValue={methods.setValue}
        />
      </div>

      {/* Section 3: การตั้งค่าเพิ่มเติม */}
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <h2 className="font-semibold text-lg">3. ການຕັ້ງຄ່າເພີ່ມເຕີມ</h2>

        <div className="space-y-4">
          <div data-tourid="form-pass-criteria">
            <FormTextarea
              name="passCriteria"
              label="ເກນຜ່ານ"
              placeholder="ຕົວຢ່າງ: ຜ່ານທຸກຂໍ້, ຜ່ານຢ່າງໜ້ອຍ 80% (ບໍ່ບັງຄັບ)"
              rows={3}
            />
          </div>
          <div data-tourid="form-fail-criteria">
            <FormTextarea
              name="failCriteria"
              label="ເກນບໍ່ຜ່ານ"
              placeholder="ຕົວຢ່າງ: ມີຂໍ້ໃດຂໍ້ໜຶ່ງບໍ່ຜ່ານ (ບໍ່ບັງຄັບ)"
              rows={3}
            />
          </div>
          <div data-tourid="form-action-on-fail">
            <FormTextarea
              name="actionOnFail"
              label="ແນວທາງເມື່ອບໍ່ຜ່ານ"
              placeholder="ຕົວຢ່າງ: ແກ້ໄຂທັນທີ, ສົ່ງໃຫ້ຫົວໜ້າ (ບໍ່ບັງຄັບ)"
              rows={3}
            />
          </div>
          <div data-tourid="form-notify-position">
            <FormInfiniteCombobox<PositionItem>
              name="notifyPositionId"
              label="ສົ່ງແຈ້ງໃຫ້ (ເມື່ອບໍ່ຜ່ານ)"
              queryKey={["qc-positions-notify"]}
              queryFn={(args) =>
                fetchLookupForInfinite(
                  `${config.apiUrl}/qc/positions/lookup`,
                  args,
                )
              }
              preloadQueryFn={(id) =>
                hydrateLookupItem(`${config.apiUrl}/qc/positions/lookup`, id)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກຕໍາແໜ່ງທີ່ຕ້ອງຮັບແຈ້ງ (ບໍ່ບັງຄັບ)"
              clearable
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div data-tourid="form-require-photo">
            <FormCheckbox name="requirePhoto" label="ຕ້ອງຮູບພາບ" />
          </div>
          <div data-tourid="form-require-notes">
            <FormCheckbox
              name="requireNotesOnFail"
              label="ຕ້ອງໃສ່ໝາຍເຫດເມື່ອບໍ່ຜ່ານ"
            />
          </div>
        </div>

        <div data-tourid="form-status">
          <Field name="status" label="ສະຖານະ">
            <RHF.Controller
              control={methods.control}
              name="status"
              render={({ field }) => (
                <SimpleSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={statusOptions}
                  placeholder="ເລືອກສະຖານະ"
                />
              )}
            />
          </Field>
        </div>

        <div className="space-y-4 border-t pt-4">
          <h3 className="font-medium text-sm">ການຜູກມັດ (Mapping)</h3>
          <div data-tourid="form-checklist">
            <FormInfiniteCombobox<{ id: string; name: string }>
              name="checklistId"
              label="Checklist"
              queryKey={["qc-checklists", "form", departmentId || "all"]}
              queryFn={async (args) => {
                const url = new URL(`${config.apiUrl}/qc/checklists/lookup`);
                if (args.search && args.search.trim().length > 0) {
                  url.searchParams.set("q", args.search);
                }
                if (departmentId) {
                  url.searchParams.set("departmentId", departmentId);
                }
                const limit = 20;
                const page =
                  typeof args.pageParam === "number" && args.pageParam > 0
                    ? args.pageParam
                    : 1;
                const skip = (page - 1) * limit;
                url.searchParams.set("limit", String(limit));
                url.searchParams.set("skip", String(skip));

                const data = await fetch(url.toString(), {
                  credentials: "include",
                }).then((r) => r.json());
                const items = Array.isArray(data.items) ? data.items : [];
                const total =
                  typeof data.total === "number" ? data.total : items.length;
                const nextPage = skip + limit < total ? page + 1 : null;

                return { items, nextPage };
              }}
              preloadQueryFn={(id) =>
                hydrateLookupItem(`${config.apiUrl}/qc/checklists/lookup`, id)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກ Checklist (ບໍ່ບັງຄັບ)"
              clearable
            />
          </div>
          <div data-tourid="form-kpi">
            <FormInfiniteCombobox<{ id: string; name: string }>
              name="kpiId"
              label="KPI"
              queryKey={["qc-kpis", "form", departmentId || "all"]}
              queryFn={async (args) => {
                const url = new URL(`${config.apiUrl}/qc/kpis/lookup`);
                if (args.search && args.search.trim().length > 0) {
                  url.searchParams.set("q", args.search);
                }
                if (departmentId) {
                  url.searchParams.set("departmentId", departmentId);
                }
                // Filter KPI ที่มี targetUnit เป็น percent
                url.searchParams.set("targetUnit", "percent");
                const limit = 20;
                const page =
                  typeof args.pageParam === "number" && args.pageParam > 0
                    ? args.pageParam
                    : 1;
                const skip = (page - 1) * limit;
                url.searchParams.set("limit", String(limit));
                url.searchParams.set("skip", String(skip));

                const data = await fetch(url.toString(), {
                  credentials: "include",
                }).then((r) => r.json());
                const items = Array.isArray(data.items) ? data.items : [];
                const total =
                  typeof data.total === "number" ? data.total : items.length;
                const nextPage = skip + limit < total ? page + 1 : null;

                return { items, nextPage };
              }}
              preloadQueryFn={(id) =>
                hydrateLookupItem(`${config.apiUrl}/qc/kpis/lookup`, id)
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
              placeholder="ເລືອກ KPI (ບໍ່ບັງຄັບ)"
              clearable
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-2 flex justify-between border-t bg-background pt-4">
        {isDev && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="mr-2 h-4 w-4" />
            Auto Fill (Sample {sampleIndex + 1}/{qcFormSamples.length})
          </Button>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleSubmit("draft")}
            disabled={submitting}
            data-tourid="form-save-draft"
          >
            ບັນທຶກເປັນຮ່າງ
          </Button>
          <Button
            type="button"
            onClick={() => handleSubmit("active")}
            isLoading={submitting}
            data-tourid="form-submit"
          >
            ບັນທຶກແລະເຜີຍແຜ່
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
